import { FormControl, MenuItem, Select } from '@material-ui/core'
import React from 'react'

import { fetchBillingMonths } from 'api/apartment'

/**
 * The month picker every revenue surface filters on.
 *
 * The options come from the BACKEND, not from a rolling window built in the
 * browser. The old version offered the last 12 calendar months, so eleven of
 * the twelve had no data behind them and the dashboards answered them honestly
 * with zeros — which read as "the page is broken" rather than "there was no
 * business that month". `/billing-months/` returns only the months that exist,
 * scoped to the apartment when one is given (Kews has Jun+Jul; Hillview only
 * has May).
 *
 * `displayEmpty` matters: without it MUI renders a Select whose value is ''
 * as a BLANK BOX, so "All months" looked like nothing was selected at all.
 */
const MonthFilter = ({
  value,
  onChange,
  allowAll = false,
  apartmentId = ''
}) => {
  const [months, setMonths] = React.useState([])

  React.useEffect(() => {
    let active = true
    fetchBillingMonths(apartmentId)
      .then((res) => active && setMonths(res))
      .catch(() => active && setMonths([]))
    return () => {
      active = false
    }
  }, [apartmentId])

  // Never render a value that is not an option — MUI logs a warning and shows
  // an empty box. If the selected month vanishes (e.g. the apartment changed),
  // fall back to the newest month that does exist.
  React.useEffect(() => {
    if (!months.length) return
    const known = months.some((m) => m.value === value)
    if (!known && !(allowAll && value === '')) {
      onChange(allowAll ? '' : months[0].value)
    }
    // Only re-run when the option list changes.
  }, [months]) // eslint-disable-line

  return (
    <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
      <Select
        value={value}
        displayEmpty
        onChange={(e) => onChange(e.target.value)}
        renderValue={(v) =>
          v ? months.find((m) => m.value === v)?.label || v : 'All months'
        }
      >
        {allowAll && <MenuItem value="">All months</MenuItem>}
        {months.map((m) => (
          <MenuItem key={m.value} value={m.value}>
            {m.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export default MonthFilter
